<template>
    <div class="step-planning-v3 custom-scrollbar">
        <!-- Top Date Selection Header -->
        <div class="ppv2-bg-[#505c8c0d] ppv2-p-6 ppv2-rounded-xl">
            <div class="planning-header-v3">
                <div class="date-selector-wrapper">
                    <button
                        class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-text-black ppv2-py-2 ppv2-px-4 ppv2-rounded-xl poppinsMedium hover:ppv2-border-[#7d2dbd] ppv2-text-base"
                        @click="toggleDatePicker"
                    >
                        <span class="ppv2-capitalize">{{ formattedFullDate }}</span>
                        <CalendarDays :size="26" />
                    </button>
                    <DatePicker
                        :value="selectedDate"
                        @input="onDateInput"
                        ref="datePicker"
                        class="hidden-date-picker"
                        type="date"
                        :value-type="'date'"
                        :disabled-date="isDateDisabled"
                        :lang="calendarLang"
                        @calendar-change="onCalendarChange"
                        :popup-class="'base-date-input-popup'"
                        :append-to-body="true"
                    >
                        <template v-slot:input>
                            <input type="hidden" />
                        </template>
                    </DatePicker>
                </div>
                <button
                    class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-text-black ppv2-py-2 ppv2-px-4 ppv2-rounded-xl poppinsMedium hover:ppv2-border-[#7d2dbd] ppv2-text-base"
                    @click="goToday"
                >
                    Aujourd'hui
                </button>
            </div>

            <!-- Horizontal Week Scroller -->
            <div class="week-scroller-v3">
                <button class="scroll-arrow prev" @click="moveWeek(-7)">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
                <div class="days-container ppv2-flex-wrap ppv2-justify-center">
                    <div
                        v-for="day in visibleDays"
                        :key="day.dateStr"
                        class="day-card ppv2-bg-white dark:ppv2-bg-dark-popup"
                        :class="{
                            active: isSelected(day.date),
                            today: isToday(day.date),
                            disabled: !day.isOpen,
                        }"
                        @click="day.isOpen && selectDay(day.date)"
                    >
                        <span class="poppinsMedium ppv2-capitalize ppv2-text-sm">{{ day.weekday }}</span>
                        <span class="poppinsSemiBold ppv2-text-xl">{{ day.dayNum }}</span>
                    </div>
                </div>
                <button class="scroll-arrow next" @click="moveWeek(7)">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </button>
            </div>

            <!-- Event Suggestions (Prochaines dates) -->
            <div
                v-if="itemDetails?.subcategory_slug === 'events' && visibleEventSuggestions.length > 0"
                class="suggestions-section-v3"
            >
                <h4 class="section-title-v3 ppv2-text-black ppv2-text-opacity-70">Prochaines dates disponible</h4>
                <div class="suggestions-grid-v3 custom-scrollbar-h">
                    <button
                        v-for="(suggestion, idx) in visibleEventSuggestions"
                        :key="idx"
                        class="suggestion-card-v3"
                        @click="selectSuggestion(suggestion)"
                    >
                        <span class="suggestion-date"
                            >{{ formatSuggestionDate(suggestion.day) }} {{ suggestion.start_time }}</span
                        >
                        <span class="suggestion-price">{{ suggestion.price_slot }}€</span>
                    </button>
                </div>
            </div>

            <!-- Time Slots Grid -->
            <div class="slots-section-v3 ppv2-mb-8">
                <div v-if="loading" class="slots-loading-v3">
                    <div class="spinner-v3"></div>
                    <p>Calcul des disponibilités...</p>
                </div>

                <template v-else>
                    <!-- Booking Mode: Grouped by Space -->
                    <div
                        v-if="
                            (itemDetails?.subcategory_slug === 'booking' || itemDetails?.subcategory_slug === 'paintball') &&
                            slotsBySpace.length > 0
                        "
                        class="booking-slots-container"
                    >
                        <div v-for="spaceGroup in slotsBySpace" :key="spaceGroup.space.id" class="space-group-v3">
                            <h4 class="space-title-v3">{{ spaceGroup.activity }}</h4>
                            <div
                                v-for="part in groupByDaypart(spaceGroup.openedTimes)"
                                :key="spaceGroup.space.id + '-' + part.key"
                                class="daypart-group-v3"
                            >
                                <h5 class="daypart-title-v3">
                                    {{ $t(part.labelKey) }}
                                    <span class="daypart-count-v3">{{ part.slots.length }}</span>
                                </h5>
                                <div class="slots-grid-v3">
                                    <button
                                        v-for="slot in part.slots"
                                        :key="slotKey(slot) + '-' + spaceGroup.space.id"
                                        class="slot-card-v3 ppv2-bg-[#5932EA] ppv2-bg-opacity-10"
                                        :class="{
                                            selected: isSlotSelected(slot, spaceGroup.space),
                                            disabled: !isSlotSelectable(slot),
                                            insufficient: isSlotInsufficient(slot),
                                            full: isSlotFull(slot),
                                        }"
                                        @click="isSlotSelectable(slot) && selectSlot(slot, spaceGroup.space)"
                                    >
                                        <span class="slot-time-v3">{{ formatTime(slot.start_time || slot.start) }}</span>
                                        <span class="slot-label-v3" v-if="slot.label">{{ slot.label }}</span>
                                        <span class="slot-price-v3" v-if="isSlotSelectable(slot) && !isSlotFull(slot)">
                                            {{ formatSlotPrice(slot) }}
                                        </span>
                                        <span class="slot-spots-v3" v-if="showSlotCount(slot)">
                                            {{ slot.nb_available }}/{{ slot.max_capacity }}
                                        </span>
                                        <span class="slot-status-v3" v-else-if="isSlotFull(slot) || !isSlotSelectable(slot)">
                                            {{ isSlotFull(slot) ? 'Complet' : 'Indispo.' }}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- VR / standard : une seule liste, découpée elle aussi par moment. -->
                    <div v-else-if="slots.length > 0">
                        <div v-for="part in groupByDaypart(slots)" :key="part.key" class="daypart-group-v3">
                            <h5 class="daypart-title-v3">
                                {{ $t(part.labelKey) }}
                                <span class="daypart-count-v3">{{ part.slots.length }}</span>
                            </h5>
                            <div class="slots-grid-v3">
                                <button
                                    v-for="slot in part.slots"
                                    :key="slotKey(slot) + '-' + (slot.spaceId || '')"
                                    class="slot-card-v3 ppv2-bg-[#5932EA] ppv2-bg-opacity-10"
                                    :class="{
                                        selected: isSlotSelected(slot),
                                        disabled: !isSlotSelectable(slot),
                                        insufficient: isSlotInsufficient(slot),
                                        full: isSlotFull(slot),
                                    }"
                                    @click="isSlotSelectable(slot) && selectSlot(slot)"
                                >
                                    <span class="slot-time-v3">{{ formatTime(slot.start_time || slot.start) }}</span>
                                    <span class="slot-label-v3" v-if="slot.label">{{ slot.label }}</span>
                                    <span class="slot-price-v3" v-if="isSlotSelectable(slot) && !isSlotFull(slot)">
                                        {{ formatSlotPrice(slot) }}
                                    </span>
                                    <span class="slot-spots-v3" v-if="showSlotCount(slot)">
                                        {{ slot.nb_available }}/{{ slot.max_capacity }}
                                    </span>
                                    <span class="slot-status-v3" v-else-if="isSlotFull(slot) || !isSlotSelectable(slot)">
                                        {{ isSlotFull(slot) ? 'Complet' : 'Indispo.' }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-else class="no-slots-v3">
                        <div class="empty-state-icon">📅</div>
                        <h3>Aucun créneau</h3>
                        <p>Désolé, aucun créneau n'est disponible pour cette date et ce nombre de participants.</p>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { CalendarDays } from 'lucide-vue'

/**
 * Découpage de la journée. Bornes hautes exclusives : matin < 12 h, après-midi < 18 h, le reste
 * au soir. Regroupées ici pour qu'un ajustement se fasse en un seul endroit.
 */
const DAYPARTS = [
    { key: 'morning', labelKey: 'devis.v3.daypart_morning', until: 12 },
    { key: 'afternoon', labelKey: 'devis.v3.daypart_afternoon', until: 18 },
    { key: 'evening', labelKey: 'devis.v3.daypart_evening', until: 24 },
]

export default {
    name: 'StepPlanning',
    components: { DatePicker, CalendarDays },
    props: {
        selectedItem: { type: Object, required: true },
        itemDetails: { type: Object, default: null },
        pax: { type: Number, default: 1 },
        forceMixing: { type: Boolean, default: false },
        durationId: { type: [Number, String], default: null },
        ticketSelections: { type: Object, default: () => ({}) },
        value: { type: Object, default: null },
        // Mode de composition du devis. Défaut `true` = comportement historique inchangé.
        usesTimeSlots: { type: Boolean, default: true },
        // Date d'ouverture au format 'YYYY-MM-DD'. Utilisée pour rouvrir le planning sur la
        // date d'une ligne existante ; absente, on ouvre sur aujourd'hui comme avant.
        initialDate: { type: String, default: null },
    },
    data() {
        // Ancre du calendrier : la date passée en prop, sinon aujourd'hui.
        const anchor = this.initialDate ? moment(this.initialDate, 'YYYY-MM-DD') : moment()

        return {
            selectedDate: anchor.toDate(), // Use native Date for stability with DatePicker
            // On stocke ces dates en chaînes 'YYYY-MM-DD' (et non en objets moment) :
            // mettre un moment dans le data réactif force Vue à l'observer en profondeur
            // (y compris son `_locale` partagé) → grosse surcharge réactive pouvant figer
            // l'onglet. Les moments sont recréés à la volée là où nécessaire.
            weekStartDate: anchor.clone().startOf('day').format('YYYY-MM-DD'),
            slots: [],
            slotsBySpace: [],
            eventSuggestions: [],
            loading: false,
            dayAvailability: {},
            currentCalendarMonth: anchor.clone().startOf('month').format('YYYY-MM-DD'),
            calendarLang: {
                formatLocale: {
                    firstDayOfWeek: 1,
                    months: [
                        'Janvier',
                        'Février',
                        'Mars',
                        'Avril',
                        'Mai',
                        'Juin',
                        'Juillet',
                        'Août',
                        'Septembre',
                        'Octobre',
                        'Novembre',
                        'Décembre',
                    ],
                    monthsShort: ['Jan.', 'Fév.', 'Mar.', 'Avr.', 'Mai', 'Juin', 'Jui.', 'Aoû.', 'Sep.', 'Oct.', 'Nov.', 'Déc.'],
                    weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
                    weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
                    weekdaysMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
                },
            },
        }
    },
    computed: {
        isSimpleTicket() {
            return this.itemDetails?.subcategory_slug === 'simpleTicket'
        },
        selectedDateMoment() {
            return moment(this.selectedDate)
        },
        formattedFullDate() {
            return this.selectedDateMoment.locale('fr').format('dddd D MMMM')
        },
        // L'échéance du devis ne borne plus les dates proposées : elle fixe le délai
        // d'acceptation du client, pas la période planifiable (cf. QuoteScheduleGuard).
        visibleEventSuggestions() {
            return this.eventSuggestions
        },
        visibleDays() {
            const days = []
            let current = moment(this.weekStartDate)
            const today = moment().startOf('day')

            for (let i = 0; i < 9; i++) {
                const dateStr = current.format('YYYY-MM-DD')
                const isPast = current.isBefore(today)

                days.push({
                    date: moment(current),
                    dateStr: dateStr,
                    weekday: current.locale('fr').format('ddd').replace('.', ''),
                    dayNum: current.format('DD'),
                    isOpen: !isPast && this.dayAvailability[dateStr] !== false,
                })
                current.add(1, 'day')
            }
            return days
        },
    },
    watch: {
        selectedDate: {
            handler: 'loadSlots',
            immediate: true,
        },
        weekStartDate: {
            handler(val) {
                const month = moment(val).startOf('month').format('YYYY-MM-DD')
                if (this.currentCalendarMonth !== month) {
                    this.currentCalendarMonth = month
                }
            },
            immediate: true,
        },
        currentCalendarMonth: {
            handler() {
                this.loadMonthAvailability()
                this.fetchEventSuggestions()
            },
            immediate: true,
        },
        pax: {
            handler() {
                this.loadSlots()
                this.fetchEventSuggestions()
            },
        },
        // À l'AJOUT, itemDetails est déjà résolu avant que cette étape ne soit montée : ce
        // watcher ne se déclenche jamais. À la RÉOUVERTURE d'une ligne, l'étape est montée
        // pendant le chargement des détails ; loadSlots sort alors en early-return
        // (`!itemDetails?.duration_id`) et rien ne la relancerait sans ceci.
        itemDetails: {
            handler(val, oldVal) {
                if (val && !oldVal) {
                    this.loadSlots()
                    this.loadMonthAvailability()
                }
            },
        },
    },
    methods: {
        async loadMonthAvailability() {
            if (!this.selectedItem) return

            try {
                const today = moment().startOf('day')
                let start = moment(this.currentCalendarMonth).startOf('month')
                if (start.isBefore(today)) start = today

                const end = moment(this.currentCalendarMonth).endOf('month').format('YYYY-MM-DD')

                const nextStart = moment(this.currentCalendarMonth).add(1, 'month').startOf('month').format('YYYY-MM-DD')
                const nextEnd = moment(this.currentCalendarMonth).add(1, 'month').endOf('month').format('YYYY-MM-DD')

                const [resCurrent, resNext] = await Promise.all([
                    axios.get(`/back-office/v3/devis/items/${this.selectedItem.id}/availability`, {
                        params: {
                            type: this.itemDetails?.type || 'experience',
                            startDate: start.format('YYYY-MM-DD'),
                            endDate: end,
                        },
                    }),
                    axios.get(`/back-office/v3/devis/items/${this.selectedItem.id}/availability`, {
                        params: { type: this.itemDetails?.type || 'experience', startDate: nextStart, endDate: nextEnd },
                    }),
                ])

                this.dayAvailability = { ...this.dayAvailability, ...resCurrent.data, ...resNext.data }
            } catch (error) {
                console.error('Error loading month availability:', error)
            }
        },
        isDateDisabled(date) {
            const mDate = moment(date)
            // Block past dates
            if (mDate.isBefore(moment().startOf('day'))) {
                return true
            }
            const dateStr = mDate.format('YYYY-MM-DD')
            if (this.dayAvailability.hasOwnProperty(dateStr)) {
                return !this.dayAvailability[dateStr]
            }
            // If not loaded, default to not disabled, but they will be once loaded
            return false
        },
        onCalendarChange(date) {
            // Garde-fou : ne réassigner que si le mois change réellement, sinon le
            // datepicker peut relancer en boucle loadMonthAvailability/fetchEventSuggestions.
            const month = moment(date).startOf('month').format('YYYY-MM-DD')
            if (this.currentCalendarMonth !== month) {
                this.currentCalendarMonth = month
            }
        },
        onDateInput(date) {
            if (date) {
                if (moment(this.selectedDate).isSame(moment(date), 'day')) return

                this.selectedDate = date
                this.weekStartDate = moment(date).startOf('day').format('YYYY-MM-DD')
            }
        },
        async loadSlots() {
            const subcategory = this.itemDetails?.subcategory_slug
            const isPaintball = subcategory === 'paintball'
            const isBooking = subcategory === 'booking'
            const isVr = subcategory === 'vr'
            const isEvent = subcategory === 'events'
            const isSimpleTicket = subcategory === 'simpleTicket'

            if (!this.selectedItem || !this.itemDetails?.duration_id) return

            // Allow only specific types for slots
            if (!isBooking && !isPaintball && !isVr && !isEvent && !isSimpleTicket) {
                this.slots = []
                this.slotsBySpace = []
                return
            }

            this.loading = true

            try {
                // Ensure availability is loaded first as requested
                const dateStr = this.selectedDateMoment.format('YYYY-MM-DD')
                if (!this.dayAvailability.hasOwnProperty(dateStr)) {
                    await this.loadMonthAvailability()
                }

                this.slots = []
                this.slotsBySpace = []

                const response = await axios.get(`/back-office/v3/devis/items/${this.selectedItem.id}/slots`, {
                    params: {
                        date: dateStr,
                        duration_id: this.durationId || this.itemDetails.duration_id,
                        durationId: this.durationId || this.itemDetails.duration_id,
                        numberOfParticipation: this.pax,
                        type: this.itemDetails.type || 'experience',
                        activity_type: this.itemDetails.activity_type || 'booking',
                        slug: this.itemDetails.slug,
                        duration: this.itemDetails.duration_minutes,
                        forceMixing: this.forceMixing ? 1 : 0,
                        list_activites: isSimpleTicket ? JSON.stringify(this.ticketSelections) : null,
                    },
                })

                const days = response.data?.data || []

                if (isBooking || isPaintball) {
                    const mergedSpaces = {}
                    days.forEach((dayData) => {
                        const spaces = dayData.spaces || []
                        spaces.forEach((space) => {
                            if (!mergedSpaces[space.id]) {
                                mergedSpaces[space.id] = { ...space, openedTimes: [] }
                            }
                            // Merge slots, adding day context if needed
                            const slotsWithDay = space.openedTimes.map((s) => ({ ...s, date: dayData.day }))
                            mergedSpaces[space.id].openedTimes.push(...slotsWithDay)
                        })
                    })
                    this.slotsBySpace = Object.values(mergedSpaces)
                } else {
                    const mergedSlots = []
                    days.forEach((dayData) => {
                        const slots = dayData.openedTimes || []
                        const slotsWithDay = slots.map((s) => ({ ...s, date: dayData.day }))
                        mergedSlots.push(...slotsWithDay)
                    })
                    this.slots = mergedSlots
                }
            } catch (error) {
                console.error('Error loading slots:', error)
            } finally {
                this.loading = false
            }
        },
        selectDay(date) {
            this.selectedDate = date.toDate()
        },
        isSelected(date) {
            return date.isSame(this.selectedDateMoment, 'day')
        },
        isToday(date) {
            return date.isSame(moment(), 'day')
        },
        moveWeek(delta) {
            this.weekStartDate = moment(this.weekStartDate).add(delta, 'days').format('YYYY-MM-DD')
        },
        goToday() {
            this.selectedDate = new Date()
            this.weekStartDate = moment().startOf('day').format('YYYY-MM-DD')
        },
        /**
         * Date réelle d'un créneau affiché : les horaires d'après minuit (< 5h) appartiennent
         * au lendemain de la journée d'exploitation en cours.
         */
        slotDate(slot) {
            const startTime = slot.start || slot.start_time || ''
            const hour = parseInt(String(startTime).split(':')[0])
            return hour < 5
                ? this.selectedDateMoment.clone().add(1, 'day').format('YYYY-MM-DD')
                : this.selectedDateMoment.format('YYYY-MM-DD')
        },
        selectSlot(slot, space = null) {
            const startTime = slot.start || slot.start_time
            const date = this.slotDate(slot)

            // Normalize data for emission
            const normalizedSlot = {
                ...slot,
                start: startTime,
                end: slot.end || slot.end_time,
                is_available: slot.is_available ?? slot.status === 'available',
                price: slot.price ?? slot.price_slot,
                date: date,
                spaceId: space ? space.id : slot.spaceId || (slot.space ? slot.space.id : null),
                spaceName: space ? space.name : slot.spaceName || (slot.space ? slot.space.name : null),
            }
            this.$emit('input', normalizedSlot)
        },
        isSlotSelected(slot, space = null) {
            if (!this.value) return false
            // Une heure peut arriver en 'HH:mm' ou 'HH:mm:ss' selon la source (créneau émis par
            // cette étape vs créneau reconstruit depuis une ligne existante) : on compare sur
            // 'HH:mm'. Les timestamps numériques passent tels quels — les tronquer créerait de
            // faux positifs entre créneaux d'une même journée.
            const norm = (v) => (typeof v === 'string' && v.includes(':') ? v.substring(0, 5) : v)
            const currentStart = norm(slot.start_timestamp || slot.start_time)
            const selectedStart = norm(this.value.start_timestamp || this.value.start_time)

            // Sans ce contrôle, l'heure seule fait correspondre le 16h du 8 et le 16h du 9 :
            // le créneau d'un AUTRE jour apparaissait sélectionné dès qu'on changeait de date.
            if (this.value.date && this.value.date !== this.slotDate(slot)) {
                return false
            }

            const currentSpaceId = space ? space.id : slot.spaceId || (slot.space ? slot.space.id : null)
            const selectedSpaceId = this.value.spaceId || (this.value.space ? this.value.space.id : null)

            // Quand les deux côtés portent une borne datée, on compare l'identité complète du
            // créneau : deux plages personnalisées peuvent partager la même heure de début, et
            // comparer ce seul champ en sélectionnait deux à la fois.
            const canCompareIdentity = (slot.start_at || slot.end_at) && (this.value.start_at || this.value.end_at)
            const sameSlot = canCompareIdentity ? this.slotKey(slot) === this.slotKey(this.value) : currentStart === selectedStart

            if (currentSpaceId && selectedSpaceId) {
                return sameSlot && currentSpaceId === selectedSpaceId
            }
            return sameSlot
        },
        // ---- Slot availability helpers --------------------------------
        // The backend `status` is AUTHORITATIVE : getItemSlots() builds it with
        // the SAME TimeSlotService that add-item re-validates against, so a slot
        // flagged here as bookable is the exact contract add-item will accept.
        // A slot returned with status "not_available" (out-of-hours / overlap)
        // or "too_early" (must_reserved_before) is never bookable even though it
        // keeps a positive `nb_available` ; the count only gauges remaining
        // places for an *open* slot. `is_nb_available` is set false by the
        // backend alongside the status (belt-and-suspenders) ; `is_available`
        // is the legacy fallback when no status is provided.
        isSlotAvailable(slot) {
            if (slot.is_nb_available === false) return false
            if (slot.status) return slot.status === 'available'
            return slot.is_available !== false
        },
        // A past / too-early slot (PLPRB-2447) : greyed, shows "Indispo."
        isSlotPast(slot) {
            return slot.status === 'too_early'
        },
        // No remaining places at all (e.g. 0/10, or players already booked) :
        // greyed, shows "Complet". A full slot is ALSO returned by the backend
        // as status "not_available", so we decide "Complet" on the remaining
        // count — NOT on the status. The count alone separates the two cases :
        //   - full           -> nb_available <= 0            -> "Complet"
        //   - closed / hours  -> nb_available == full capacity -> "Indispo."
        isSlotFull(slot) {
            if (slot.nb_available !== undefined && slot.nb_available !== null) {
                return slot.nb_available <= 0
            }
            return false
        },
        // Some places left, but fewer than the selected number of participants
        // (e.g. 6/10 while booking for 7) : greyed, still shows the count.
        // NOTE: the backend folds "not enough places for this pax" into
        // status "not_available" (TimeSlotService: availablePlaces < nbPlaces),
        // so we DON'T gate this on the status — only on the remaining count.
        // `0 < nb_available < pax` reliably means an open, partially-booked slot
        // (a closed/out-of-hours slot still advertises its full capacity).
        isSlotInsufficient(slot) {
            return (
                slot.nb_available !== undefined &&
                slot.nb_available !== null &&
                slot.nb_available > 0 &&
                slot.nb_available < this.pax
            )
        },
        // Whether the slot can actually be booked for the current pax.
        // Mirrors the add-item guards so the user cannot pick a slot the
        // backend would reject (e.g. "Vous ne pouvez pas réserver avant…").
        isSlotSelectable(slot) {
            // Mode SANS créneau : le devis ne pose aucune réservation, donc aucune capacité
            // n'est consommée. Les refus liés aux PLACES (complet, places insuffisantes, pax
            // au-delà de la capacité) ne s'appliquent pas — le créneau n'est qu'un horaire
            // indicatif. Les contraintes d'HORAIRES, elles, restent bloquantes : un créneau
            // hors ouverture ou trop proche n'a pas plus de sens ici qu'ailleurs.
            if (!this.usesTimeSlots) {
                if (this.isSlotPast(slot)) return false
                return this.isSlotAvailable(slot) || this.isSlotFull(slot) || this.isSlotInsufficient(slot)
            }

            // Backend status first: an out-of-hours / too-early slot is never
            // selectable, regardless of how many places it still advertises.
            if (!this.isSlotAvailable(slot)) return false
            if (this.isSlotPast(slot) || this.isSlotFull(slot)) return false
            // Requested pax exceeds the slot's hard capacity → add-item rejects.
            if (slot.max_capacity !== undefined && slot.max_capacity !== null && this.pax > slot.max_capacity) {
                return false
            }
            if (slot.nb_available !== undefined && slot.nb_available !== null) {
                return slot.nb_available >= this.pax
            }
            return true
        },
        // Whether to render the "X/Y" remaining-places badge.
        // Shown when the slot has places that are RELEVANT to the user:
        //   - selectable                         -> count + price
        //   - some places but fewer than pax     -> count in amber (insufficient)
        // A 0-place slot is "Complet"; a closed slot that still advertises
        // >= pax places (out-of-hours / past) shows "Indispo." instead.
        showSlotCount(slot) {
            if (slot.nb_available === undefined || slot.nb_available === null) return false
            if (slot.nb_available <= 0) return false
            return this.isSlotSelectable(slot) || this.isSlotInsufficient(slot)
        },
        /**
         * Identité d'un créneau pour les clés Vue et la comparaison de sélection.
         *
         * L'heure de début seule ne suffit plus : deux plages personnalisées peuvent commencer à la
         * même heure (« Matin » 09:00→12:00 et « Journée » 09:00→18:00), ce qui produisait des clés
         * dupliquées et sélectionnait visuellement les deux tuiles.
         */
        slotKey(slot) {
            const start = slot.start_at || slot.start_time || slot.start || ''
            const end = slot.end_at || slot.end_time || slot.end || ''
            return `${start}|${end}`
        },
        /**
         * Heure de début d'un créneau, en entier.
         *
         * `start_time` commence toujours par « HH:mm » — c'est déjà l'hypothèse de formatTime,
         * qui se contente d'en prendre les cinq premiers caractères.
         */
        slotHour(slot) {
            const raw = slot.start_time || slot.start || ''
            const hour = parseInt(String(raw).substring(0, 2), 10)

            return Number.isFinite(hour) ? hour : 0
        },
        /**
         * Répartit les créneaux en Matin / Après-midi / Soir.
         *
         * Une journée d'ouverture large produit vite trente tuiles ; les présenter d'un bloc
         * oblige à balayer toute la grille pour trouver « un créneau en fin de journée ».
         *
         * Un moment SANS créneau n'est pas rendu : un titre suivi du vide n'apprend rien et
         * ferait croire à un problème de chargement. L'ordre des tuiles à l'intérieur d'un
         * moment est celui de l'API — on ne retrie pas, on découpe.
         */
        groupByDaypart(list) {
            const buckets = DAYPARTS.map((part) => ({ ...part, slots: [] }))
            const evening = buckets[buckets.length - 1]
            let previousHour = null
            let pastMidnight = false

            ;(list || []).forEach((slot) => {
                const hour = this.slotHour(slot)

                // Une journée d'ouverture peut franchir MINUIT. L'API renvoie les créneaux dans
                // l'ordre chronologique : dès que l'heure redescend par rapport au créneau
                // précédent, on est passé au lendemain. Tout ce qui suit prolonge la soirée en
                // cours — le ranger au « Matin » le renverrait en tête de liste, loin des
                // créneaux qu'il continue, et donnerait l'impression qu'il a disparu.
                if (previousHour !== null && hour < previousHour) {
                    pastMidnight = true
                }
                previousHour = hour

                const bucket = pastMidnight ? evening : buckets.find((part) => hour < part.until) || evening
                bucket.slots.push(slot)
            })

            return buckets.filter((part) => part.slots.length > 0)
        },
        formatTime(timeStr) {
            if (!timeStr) return '--:--'
            return timeStr.substring(0, 5)
        },
        // Montant affiché sur une tuile de créneau.
        //
        // Cas général (booking / paintball / vr / events) : `formatStandardSlot` renvoie
        // `priceDetails["price"]`, un prix UNITAIRE par participant → on multiplie par le pax.
        //
        // simpleTicket : `price_slot` vaut déjà le TOTAL de la sélection, car
        // SimpleTicketService::getSumPricePrestationOptionsQuantities somme `prix × quantité`
        // sur chaque type de billet. Le multiplier par le pax (= Σ des quantités) comptait donc
        // le montant deux fois (3×12 + 2×8 = 52 € affichés 260 € pour pax = 5).
        formatSlotPrice(slot) {
            const raw = slot.price_slot || slot.price
            if (!raw) return '--€'
            const total = this.isSimpleTicket ? parseFloat(raw) : parseFloat(raw) * this.pax
            return total.toFixed(2) + '€'
        },
        toggleDatePicker() {
            this.$refs.datePicker.openPopup()
        },
        selectSuggestion(suggestion) {
            this.selectedDate = new Date(suggestion.day)
            this.weekStartDate = moment(suggestion.day).startOf('day').format('YYYY-MM-DD')

            // Wait for slots to load from date change, then select the specific slot
            setTimeout(() => {
                const targetSlot = this.slots.find((s) => s.start_time === suggestion.start_time)
                if (targetSlot) {
                    this.selectSlot(targetSlot)
                }
            }, 500)
        },
        async fetchEventSuggestions() {
            if (this.itemDetails?.subcategory_slug !== 'events' || !this.selectedItem) {
                this.eventSuggestions = []
                return
            }

            try {
                const response = await axios.get(`/back-office/v3/devis/items/${this.selectedItem.id}/next-available-dates`, {
                    params: { numberOfParticipation: this.pax },
                })

                // Flatten the multi-day response into individual suggestions
                const flattened = []
                const days = response.data?.data || []
                days.forEach((dayData) => {
                    dayData.openedTimes.forEach((slot) => {
                        if (slot.status === 'available') {
                            flattened.push({
                                day: dayData.day,
                                ...slot,
                            })
                        }
                    })
                })
                this.eventSuggestions = flattened.slice(0, 6)
            } catch (error) {
                console.error('Error fetching event suggestions:', error)
            }
        },
        formatSuggestionDate(date) {
            return moment(date).locale('fr').format('ddd. D MMM')
        },
    },
}
</script>

<style scoped>
.step-planning-v3 {
    height: 100%;
    padding: 24px;
    display: flex;
    flex-direction: column;
}

/* Header */
.planning-header-v3 {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-bottom: 30px;
}

.date-selector-wrapper {
    position: relative;
}

.hidden-date-picker {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

/* Week Scroller */
.week-scroller-v3 {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 40px;
    justify-content: center;
}

.scroll-arrow {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.scroll-arrow:hover {
    background: #f1f5f9;
    color: #7d2dbd;
}

.days-container {
    display: flex;
    gap: 12px;
}

.day-card {
    width: 76px;
    height: 70px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 24px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid transparent;
    gap: 6px;
}

.day-card span {
    line-height: 1;
}

.day-card:hover:not(.active) {
    background: rgb(125, 45, 189, 0.2) !important;
    transform: translateY(-2px);
}

.day-card.active {
    background: #7d2dbd !important;
    color: white;
    box-shadow: 0 8px 16px rgba(125, 45, 189, 0.2);
}

.day-card.active .day-name,
.day-card.active .day-number {
    color: white;
}

.day-card.today:not(.active) {
    border-color: #7d2dbd;
}

.day-card.disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.day-card.disabled:hover {
    transform: none;
}

/* Slots Grid */
.slots-section-v3 {
    flex: 1;
    min-height: 0;
}

.slots-grid-v3 {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 12px;
    margin-bottom: 65px;
}

/* Event Suggestions */
.suggestions-section-v3 {
    margin-bottom: 24px;
    padding: 0 4px;
}

.section-title-v3 {
    font-size: 14px;
    font-weight: 700;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.suggestions-grid-v3 {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
}

.suggestion-card-v3 {
    flex: 0 0 auto;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    padding: 10px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 130px;
}

.suggestion-card-v3:hover {
    border-color: #7d2dbd;
    background: #fdf4ff;
    transform: translateY(-2px);
}

.suggestion-date {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
    white-space: nowrap;
}

.suggestion-price {
    font-size: 12px;
    font-weight: 600;
    color: #7d2dbd;
}

.custom-scrollbar-h::-webkit-scrollbar {
    height: 4px;
}

.custom-scrollbar-h::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
}

.custom-scrollbar-h::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}

.slot-card-v3 {
    border: none;
    border-radius: 12px;
    padding: 12px 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 2px;
    cursor: pointer;
    transition: all 0.2s;
}

.slot-time-v3 {
    font-size: 15px;
    font-weight: 700;
}

.slot-price-v3 {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
}

.slot-status-v3 {
    font-size: 11px;
    color: #94a3b8;
}

.slot-spots-v3 {
    font-size: 11px;
    font-weight: 600;
    color: #10b981;
}

.slot-card-v3.selected .slot-spots-v3 {
    color: white;
}

.slot-card-v3:hover:not(.disabled) {
    --tw-bg-opacity: 0.2;
    transform: scale(1.05);
}

.slot-card-v3.selected {
    background: #7d2dbd;
    box-shadow: 0 4px 12px rgba(125, 45, 189, 0.3);
}

.slot-card-v3.selected .slot-time-v3,
.slot-card-v3.selected .slot-price-v3 {
    color: white;
}

.slot-card-v3.disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

/* No place left at all -> "Complet" */
.slot-card-v3.full {
    background: #fef2f2;
    border-color: #fecaca;
}

/* Créneau complet ET sélectionné : c'est le cas d'une ligne rouverte dont les places sont
   déjà prises — souvent par elle-même. `.full` étant déclaré après `.selected`, il écrasait
   le fond violet et le créneau courant devenait indistinguable des autres. On rétablit
   l'identité "sélectionné", en gardant la mention "Complet" lisible. */
.slot-card-v3.selected.full {
    background: #7d2dbd;
    border-color: #7d2dbd;
    box-shadow: 0 4px 12px rgba(125, 45, 189, 0.3);
}

.slot-card-v3.selected.full .slot-status-v3,
.slot-card-v3.selected.full .slot-time-v3 {
    color: white;
}

.slot-card-v3.full .slot-status-v3 {
    color: #ef4444;
    font-weight: 600;
}

/* Some places left but fewer than the selected pax -> count stays visible, in amber */
.slot-card-v3.insufficient .slot-spots-v3 {
    color: #f59e0b;
}

.slots-loading-v3,
.no-slots-v3 {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 300px;
    color: #94a3b8;
}

.spinner-v3 {
    width: 32px;
    height: 32px;
    border: 3px solid #e2e8f0;
    border-top-color: #7d2dbd;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.empty-state-icon {
    font-size: 40px;
    margin-bottom: 16px;
}

.no-slots-v3 h3 {
    color: #475569;
    margin: 0 0 8px 0;
}

/* Booking Grouped Styles */
.booking-slots-container {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.space-group-v3 {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.space-title-v3 {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    padding-left: 4px;
    border-left: 4px solid #7d2dbd;
    line-height: 1.2;
}
@media (max-width: 1200px) {
    .day-card {
        width: clamp(60px, 5.837vw, 80px);
        height: clamp(60px, 5.837vw, 80px);
        gap: 0;
        border-radius: 16px;
    }
}

@media (max-width: 1000px) {
    .day-card {
        width: clamp(50px, 5.999vw, 70px);
        height: 60px;
        gap: 0;
        border-radius: 14px;
    }
}
@media (max-width: 850px) {
    .day-card {
        width: clamp(40px, 5.8799vw, 80px);
        height: 60px;
        gap: 0;
        border-radius: 12px;
    }
}

/* Découpage de la journée : un titre discret par moment, pour que la grille reste lisible
   quand une journée d'ouverture large produit trente tuiles. */
.daypart-group-v3 + .daypart-group-v3 {
    margin-top: 18px;
}

.daypart-title-v3 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
    color: #64748b;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.daypart-count-v3 {
    min-width: 20px;
    padding: 1px 6px;
    border-radius: 9999px;
    background: #eef1f6;
    color: #64748b;
    font-size: 11px;
    line-height: 1.5;
    text-align: center;
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
}
</style>
